// SequencingGame.jsx - TRP2 Trainability Sequencing Test
// Players apply priority rules to put tasks in correct order (1-4)
// Mobile-friendly click-to-rank interface

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Monitor,
  Smartphone,
  CheckCircle,
  XCircle,
  RotateCcw,
  ListOrdered,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  Info,
  Clock,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  INSTRUCTIONS: 'instructions',
  STUDYING: 'studying',
  PLAYING: 'playing',
  FEEDBACK: 'feedback',
  FINISHED: 'finished',
};

const LEVEL_LABELS = {
  1: 'Basic safety priorities',
  2: 'Emergency procedures',
  3: 'Multi-factor decisions',
  4: 'Complex scenarios',
  5: 'Expert-level sequencing',
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Sequencing scenarios with priority rules and tasks
const SEQUENCING_SCENARIOS = {
  1: [
    {
      rules: `When responding to a platform incident:
• Life-threatening situations take highest priority
• Passenger safety comes before service disruption
• Communication with control must happen before any physical action
• Documentation is always the last step`,
      tasks: [
        { id: 'a', text: 'Radio control to report the incident', correctPosition: 2 },
        { id: 'b', text: 'Administer first aid to injured passenger', correctPosition: 1 },
        { id: 'c', text: 'Complete the incident report form', correctPosition: 4 },
        { id: 'd', text: 'Clear other passengers from the area', correctPosition: 3 },
      ],
    },
    {
      rules: `For a minor equipment fault that does not affect safety:
• Report to maintenance first
• Continue service if safe to do so
• Monitor the fault during journey
• Log in defect book at end of shift`,
      tasks: [
        { id: 'a', text: 'Continue operating the service', correctPosition: 2 },
        { id: 'b', text: 'Report fault to maintenance control', correctPosition: 1 },
        { id: 'c', text: 'Record in defect book', correctPosition: 4 },
        { id: 'd', text: 'Monitor fault throughout journey', correctPosition: 3 },
      ],
    },
    {
      rules: `Before departing from a station:
• Ensure all doors are closed and locked
• Check platform is clear of passengers
• Receive right-away signal from dispatch
• Release brake only after all checks complete`,
      tasks: [
        { id: 'a', text: 'Release the brake and depart', correctPosition: 4 },
        { id: 'b', text: 'Check all doors are closed', correctPosition: 1 },
        { id: 'c', text: 'Receive right-away from dispatcher', correctPosition: 3 },
        { id: 'd', text: 'Visually check platform is clear', correctPosition: 2 },
      ],
    },
    {
      rules: `When arriving at a terminus station:
• Apply parking brake before any other action
• Open doors only after train has stopped completely
• Make PA announcement before opening doors
• Check platform indicator shows correct destination`,
      tasks: [
        { id: 'a', text: 'Make arrival PA announcement', correctPosition: 3 },
        { id: 'b', text: 'Apply parking brake', correctPosition: 1 },
        { id: 'c', text: 'Open passenger doors', correctPosition: 4 },
        { id: 'd', text: 'Confirm train has stopped completely', correctPosition: 2 },
      ],
    },
    {
      rules: `For shift handover procedures:
• Review any outstanding defects first
• Check fuel/power levels
• Conduct external walk-around inspection
• Sign handover document last`,
      tasks: [
        { id: 'a', text: 'Sign the handover document', correctPosition: 4 },
        { id: 'b', text: 'Review outstanding defect reports', correctPosition: 1 },
        { id: 'c', text: 'Conduct external inspection', correctPosition: 3 },
        { id: 'd', text: 'Check power/fuel levels', correctPosition: 2 },
      ],
    },
  ],
  2: [
    {
      rules: `Emergency brake activation procedure:
• Safety of passengers is paramount
• Communication before investigation
• Never attempt to move train until authorised
• Document everything for investigation`,
      tasks: [
        { id: 'a', text: 'Make PA to reassure passengers', correctPosition: 1 },
        { id: 'b', text: 'Contact signaller to report emergency brake', correctPosition: 2 },
        { id: 'c', text: 'Record time and location in log', correctPosition: 4 },
        { id: 'd', text: 'Wait for authorisation to proceed', correctPosition: 3 },
      ],
    },
    {
      rules: `Fire alarm activation on train:
• Stop train at safe location (not in tunnel)
• Passenger evacuation takes priority over all else
• Contact emergency services via control
• Only attempt firefighting if safe and trained`,
      tasks: [
        { id: 'a', text: 'Stop train at nearest safe location', correctPosition: 1 },
        { id: 'b', text: 'Use fire extinguisher if safe to do so', correctPosition: 4 },
        { id: 'c', text: 'Contact control for emergency services', correctPosition: 3 },
        { id: 'd', text: 'Begin passenger evacuation', correctPosition: 2 },
      ],
    },
    {
      rules: `Signal passed at danger (SPAD) procedure:
• Stop immediately and do not move
• Report to signaller within 30 seconds
• Await instructions - do not assume
• Complete detailed written report`,
      tasks: [
        { id: 'a', text: 'Complete SPAD incident report', correctPosition: 4 },
        { id: 'b', text: 'Bring train to immediate stop', correctPosition: 1 },
        { id: 'c', text: 'Await further instructions from signaller', correctPosition: 3 },
        { id: 'd', text: 'Contact signaller to report SPAD', correctPosition: 2 },
      ],
    },
    {
      rules: `Medical emergency on train:
• Call for any medical professionals on board
• Do not move casualty unless danger present
• Request ambulance via control
• Continue to destination unless life-threatening`,
      tasks: [
        { id: 'a', text: 'Make PA asking for medical assistance', correctPosition: 1 },
        { id: 'b', text: 'Contact control to request ambulance', correctPosition: 3 },
        { id: 'c', text: 'Assess if train should stop or continue', correctPosition: 4 },
        { id: 'd', text: 'Ensure casualty is comfortable and safe', correctPosition: 2 },
      ],
    },
    {
      rules: `Trespass on railway line reported:
• Reduce speed immediately
• Sound horn continuously
• Report to signaller with exact location
• Prepare to stop if person visible`,
      tasks: [
        { id: 'a', text: 'Sound horn continuously', correctPosition: 2 },
        { id: 'b', text: 'Begin speed reduction', correctPosition: 1 },
        { id: 'c', text: 'Prepare emergency stop if needed', correctPosition: 4 },
        { id: 'd', text: 'Report location to signaller', correctPosition: 3 },
      ],
    },
  ],
  3: [
    {
      rules: `Multiple simultaneous warnings:
• AWS warning overrides other alerts
• Door fault takes priority over comfort issues
• Traction fault assessed by severity
• Climate control is lowest priority`,
      tasks: [
        { id: 'a', text: 'Acknowledge AWS warning', correctPosition: 1 },
        { id: 'b', text: 'Address door interlock fault', correctPosition: 2 },
        { id: 'c', text: 'Reset climate control system', correctPosition: 4 },
        { id: 'd', text: 'Assess traction motor warning', correctPosition: 3 },
      ],
    },
    {
      rules: `Delayed service recovery:
• Passenger information is first priority
• Skip non-essential station stops
• Coordinate with control for connections
• Normal service resumes at terminus`,
      tasks: [
        { id: 'a', text: 'Skip request stops if no passengers waiting', correctPosition: 2 },
        { id: 'b', text: 'Announce delay and apologise to passengers', correctPosition: 1 },
        { id: 'c', text: 'Resume normal stopping pattern at terminus', correctPosition: 4 },
        { id: 'd', text: 'Liaise with control about connections', correctPosition: 3 },
      ],
    },
    {
      rules: `Adverse weather preparation:
• Check weather forecast before departure
• Adjust braking distances as needed
• Report any track conditions observed
• Reduce speed in visibility under 50m`,
      tasks: [
        { id: 'a', text: 'Check forecast and warnings', correctPosition: 1 },
        { id: 'b', text: 'Report observed conditions to control', correctPosition: 3 },
        { id: 'c', text: 'Reduce speed in poor visibility', correctPosition: 4 },
        { id: 'd', text: 'Adjust braking approach for conditions', correctPosition: 2 },
      ],
    },
    {
      rules: `Suspected security threat:
• Do not confront or challenge
• Move away and observe from distance
• Alert control using code word
• Follow instructions from security`,
      tasks: [
        { id: 'a', text: 'Move away from threat discreetly', correctPosition: 1 },
        { id: 'b', text: 'Follow security team instructions', correctPosition: 4 },
        { id: 'c', text: 'Alert control using security code', correctPosition: 3 },
        { id: 'd', text: 'Observe and note details from safe distance', correctPosition: 2 },
      ],
    },
    {
      rules: `End of service checks:
• Secure all external doors first
• Check for passengers/lost property
• Power down non-essential systems
• Complete handover documentation`,
      tasks: [
        { id: 'a', text: 'Power down auxiliary systems', correctPosition: 3 },
        { id: 'b', text: 'Lock all external access doors', correctPosition: 1 },
        { id: 'c', text: 'Complete service log and handover', correctPosition: 4 },
        { id: 'd', text: 'Walk through checking for passengers', correctPosition: 2 },
      ],
    },
  ],
  4: [
    {
      rules: `Train failure between stations:
• Passenger welfare is ongoing priority
• Obtain precise location for rescue
• Controlled evacuation only if authorised
• Maintain communication every 5 minutes`,
      tasks: [
        { id: 'a', text: 'Make PA with situation update', correctPosition: 1 },
        { id: 'b', text: 'Update control on passenger welfare', correctPosition: 4 },
        { id: 'c', text: 'Determine and report exact location', correctPosition: 2 },
        { id: 'd', text: 'Await rescue train or evacuation order', correctPosition: 3 },
      ],
    },
    {
      rules: `Conflict between procedures:
• Safety rules never overridden
• More recent instruction supersedes older
• Local rules take precedence over general
• When in doubt, stop and verify`,
      tasks: [
        { id: 'a', text: 'Apply safety-critical procedure', correctPosition: 1 },
        { id: 'b', text: 'Stop and contact control if unsure', correctPosition: 4 },
        { id: 'c', text: 'Check for local rule variations', correctPosition: 3 },
        { id: 'd', text: 'Follow most recent instruction', correctPosition: 2 },
      ],
    },
    {
      rules: `Multi-agency incident response:
• British Transport Police lead on crime
• Fire service lead on fire/rescue
• Driver assists but does not lead
• Train company coordinates passengers`,
      tasks: [
        { id: 'a', text: 'Defer to Fire service at rescue scene', correctPosition: 2 },
        { id: 'b', text: 'Await BTP for security incident', correctPosition: 1 },
        { id: 'c', text: 'Coordinate passenger welfare with TOC', correctPosition: 4 },
        { id: 'd', text: 'Assist emergency services as directed', correctPosition: 3 },
      ],
    },
    {
      rules: `Communication system failure:
• Use backup radio channel first
• Mobile phone as secondary backup
• Signal to signaller visually if possible
• Proceed with extreme caution only`,
      tasks: [
        { id: 'a', text: 'Try backup radio frequency', correctPosition: 1 },
        { id: 'b', text: 'Proceed at caution if no contact possible', correctPosition: 4 },
        { id: 'c', text: 'Attempt visual signal to signal box', correctPosition: 3 },
        { id: 'd', text: 'Use mobile phone to contact control', correctPosition: 2 },
      ],
    },
    {
      rules: `Fatigue management protocol:
• Report feeling unfit to continue
• Take break at next safe opportunity
• Do not drive if impaired
• Safety override commercial pressure`,
      tasks: [
        { id: 'a', text: 'Report fatigue to control immediately', correctPosition: 1 },
        { id: 'b', text: 'Prioritise safety over schedule', correctPosition: 4 },
        { id: 'c', text: 'Request relief driver if needed', correctPosition: 3 },
        { id: 'd', text: 'Stop at next station if severely fatigued', correctPosition: 2 },
      ],
    },
  ],
  5: [
    {
      rules: `Complex degraded mode operation:
• Signalling failure: driver authorisation required
• AWS failure: maximum 40mph, report every signal
• Combined failure: stop and request pilot
• Never assume - always verify`,
      tasks: [
        { id: 'a', text: 'Request driver authorisation for each signal', correctPosition: 2 },
        { id: 'b', text: 'Stop and request pilot for combined failure', correctPosition: 4 },
        { id: 'c', text: 'Reduce speed to 40mph maximum', correctPosition: 1 },
        { id: 'd', text: 'Report passing each signal to control', correctPosition: 3 },
      ],
    },
    {
      rules: `Major incident coordination:
• Gold command makes strategic decisions
• Silver manages tactical response
• Bronze handles immediate scene
• Driver provides information, follows Bronze`,
      tasks: [
        { id: 'a', text: 'Report situation details to Bronze commander', correctPosition: 1 },
        { id: 'b', text: 'Escalate strategic concerns via chain', correctPosition: 4 },
        { id: 'c', text: 'Execute Bronze commander directions', correctPosition: 2 },
        { id: 'd', text: 'Support Silver on resource requirements', correctPosition: 3 },
      ],
    },
    {
      rules: `Near-miss reporting priority:
• Immediate safety action first
• Verbal report within 15 minutes
• Written report within 24 hours
• Support investigation fully`,
      tasks: [
        { id: 'a', text: 'Take any immediate safety action needed', correctPosition: 1 },
        { id: 'b', text: 'Cooperate with investigation process', correctPosition: 4 },
        { id: 'c', text: 'Submit detailed written report', correctPosition: 3 },
        { id: 'd', text: 'Verbally report to control immediately', correctPosition: 2 },
      ],
    },
    {
      rules: `Crossing protection failure:
• Approach at caution, sound horn
• Stop before crossing if gates open
• Contact signaller before proceeding
• Only cross when authorised and safe`,
      tasks: [
        { id: 'a', text: 'Stop if level crossing gates are open', correctPosition: 2 },
        { id: 'b', text: 'Approach crossing at caution speed', correctPosition: 1 },
        { id: 'c', text: 'Proceed only when authorised and clear', correctPosition: 4 },
        { id: 'd', text: 'Contact signaller for instructions', correctPosition: 3 },
      ],
    },
    {
      rules: `Rolling stock defect categories:
• A defect: immediate stop, do not move
• B defect: proceed to next station only
• C defect: complete journey, report
• D defect: log and continue normal service`,
      tasks: [
        { id: 'a', text: 'Stop immediately for Category A', correctPosition: 1 },
        { id: 'b', text: 'Log Category D and continue service', correctPosition: 4 },
        { id: 'c', text: 'Finish journey and report Category C', correctPosition: 3 },
        { id: 'd', text: 'Proceed to next station for Category B', correctPosition: 2 },
      ],
    },
  ],
};

// Device Mode Selector Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Larger layout</p>
          </div>
        </Button>

        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Tablet/Phone</p>
            <p className="text-xs text-[#64748B]">Touch-friendly</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Instructions Screen
const InstructionsScreen = ({ level, onStart, deviceMode }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-6">
      <div className="text-center mb-6">
        <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
          Level {level}: {LEVEL_LABELS[level] || 'Advanced'}
        </Badge>
        <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
          Task Sequencing Test
        </h2>
        <p className="text-[#64748B]">
          Develops skills to improve performance on TRP2 Trainability Tests
        </p>
      </div>

      <div className="space-y-4 mb-6">
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">1</div>
          <p className="text-sm text-[#64748B]">Read the <strong>priority rules</strong> carefully - you have 30 seconds to study them.</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">2</div>
          <p className="text-sm text-[#64748B]">You'll see <strong>4 tasks</strong> that need to be put in the correct order.</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">3</div>
          <p className="text-sm text-[#64748B]">Use the <strong>arrow buttons</strong> to move tasks up or down in the sequence.</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">4</div>
          <p className="text-sm text-[#64748B]">Position 1 is the <strong>first action</strong>, position 4 is the <strong>last action</strong>.</p>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">Scoring</span>
        </div>
        <p className="text-sm text-amber-600 mt-1">
          Points are awarded for each task in the correct position. Partial credit for adjacent positions.
        </p>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
        <div className="flex items-center gap-2 text-blue-700">
          <Info className="w-4 h-4" />
          <span className="text-sm font-medium">RIS-3751-TOM</span>
        </div>
        <p className="text-sm text-blue-600 mt-1">
          This exercise develops skills assessed by TRP2 - measuring ability to learn rules and apply them to determine correct procedural sequences.
        </p>
      </div>

      <Button
        onClick={onStart}
        className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white"
        data-testid="begin-test-btn"
      >
        <Play className="w-4 h-4 mr-2" />
        Begin Test (5 scenarios)
      </Button>
    </CardContent>
  </Card>
);

// Study Phase Component
const StudyPhase = ({ rules, timeLeft, onTimeUp }) => {
  useEffect(() => {
    if (timeLeft <= 0) {
      onTimeUp();
    }
  }, [timeLeft, onTimeUp]);

  return (
    <Card className="border-slate-200">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <Badge className="bg-blue-100 text-blue-700">Study Phase</Badge>
          <div className="flex items-center gap-2 text-[#0B1D51]">
            <Clock className="w-4 h-4" />
            <span className="font-mono font-bold">{timeLeft}s</span>
          </div>
        </div>

        <Progress value={(timeLeft / 30) * 100} className="h-2 mb-4" />

        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="font-heading font-bold text-[#0B1D51] mb-3">Priority Rules:</h3>
          <p className="text-[#374151] whitespace-pre-line leading-relaxed">{rules}</p>
        </div>

        <p className="text-center text-sm text-[#64748B] mt-4">
          Memorise these rules - you'll need to apply them to order tasks correctly.
        </p>
      </CardContent>
    </Card>
  );
};

// Task Ordering Component
const TaskOrdering = ({ tasks, onReorder, onSubmit, canSubmit }) => {
  const moveTask = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tasks.length) return;

    const newTasks = [...tasks];
    [newTasks[index], newTasks[newIndex]] = [newTasks[newIndex], newTasks[index]];
    onReorder(newTasks);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#64748B] mb-2">
        Arrange these tasks in the correct order (1 = first action):
      </p>

      {tasks.map((task, index) => (
        <motion.div
          key={task.id}
          layout
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg shadow-sm"
        >
          <div className="w-8 h-8 rounded-full bg-[#0B1D51] text-white flex items-center justify-center font-bold">
            {index + 1}
          </div>

          <p className="flex-1 text-sm text-[#374151]">{task.text}</p>

          <div className="flex flex-col gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => moveTask(index, -1)}
              disabled={index === 0}
              data-testid={`move-up-${task.id}`}
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => moveTask(index, 1)}
              disabled={index === tasks.length - 1}
              data-testid={`move-down-${task.id}`}
            >
              <ArrowDown className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      ))}

      <Button
        onClick={onSubmit}
        disabled={!canSubmit}
        className="w-full mt-4 bg-[#22C55E] hover:bg-[#16A34A] text-white"
        data-testid="submit-sequence-btn"
      >
        <CheckCircle className="w-4 h-4 mr-2" />
        Submit Sequence
      </Button>
    </div>
  );
};

// Feedback Screen
const FeedbackScreen = ({ scenario, userOrder, score, maxScore, onNext, isLastScenario }) => {
  const getPositionFeedback = (task, userPosition) => {
    const correctPosition = task.correctPosition;
    if (userPosition === correctPosition) {
      return { correct: true, message: 'Correct position!' };
    } else if (Math.abs(userPosition - correctPosition) === 1) {
      return { correct: false, message: `Should be position ${correctPosition}` };
    } else {
      return { correct: false, message: `Should be position ${correctPosition}` };
    }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="pt-6">
        <div className="text-center mb-4">
          <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${score >= maxScore * 0.75 ? 'bg-green-100' : score >= maxScore * 0.5 ? 'bg-amber-100' : 'bg-red-100'}`}>
            {score >= maxScore * 0.75 ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            )}
          </div>
          <h3 className="font-heading text-xl font-bold text-[#0B1D51]">
            Score: {score}/{maxScore}
          </h3>
        </div>

        <div className="p-3 bg-slate-50 rounded-lg mb-4">
          <h4 className="font-medium text-[#0B1D51] mb-2">The Rules:</h4>
          <p className="text-sm text-[#64748B] whitespace-pre-line">{scenario.rules}</p>
        </div>

        <div className="space-y-2 mb-4">
          <h4 className="font-medium text-[#0B1D51]">Correct Sequence:</h4>
          {scenario.tasks
            .slice()
            .sort((a, b) => a.correctPosition - b.correctPosition)
            .map((task, index) => {
              const userPosition = userOrder.findIndex(t => t.id === task.id) + 1;
              const feedback = getPositionFeedback(task, userPosition);
              return (
                <div
                  key={task.id}
                  className={`p-3 rounded-lg border ${feedback.correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${feedback.correct ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                      {task.correctPosition}
                    </span>
                    <span className="flex-1 text-sm">{task.text}</span>
                    {!feedback.correct && (
                      <span className="text-xs text-red-600">You placed: {userPosition}</span>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        <Button
          onClick={onNext}
          className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90 text-white"
          data-testid="next-scenario-btn"
        >
          {isLastScenario ? 'View Results' : 'Next Scenario'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Main Game Component
const SequencingGame = ({ level = 1, onComplete, onExit }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [studyTimeLeft, setStudyTimeLeft] = useState(30);
  const [currentTasks, setCurrentTasks] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [maxPossibleScore, setMaxPossibleScore] = useState(0);
  const [scenarioScore, setScenarioScore] = useState(0);
  const [scenarioResults, setScenarioResults] = useState([]);
  const studyTimerRef = useRef(null);

  const scenarios = useMemo(() => {
    const levelScenarios = SEQUENCING_SCENARIOS[level] || SEQUENCING_SCENARIOS[1];
    return shuffleArray(levelScenarios).slice(0, 5);
  }, [level]);

  const currentScenario = scenarios[currentScenarioIndex];

  // Study timer effect
  useEffect(() => {
    if (gameState === GAME_STATES.STUDYING && studyTimeLeft > 0) {
      studyTimerRef.current = setTimeout(() => {
        setStudyTimeLeft(prev => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(studyTimerRef.current);
  }, [gameState, studyTimeLeft]);

  const handleDeviceSelect = (mode) => {
    setDeviceMode(mode);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  const handleStartTest = () => {
    setStudyTimeLeft(30);
    setGameState(GAME_STATES.STUDYING);
  };

  const handleStudyComplete = () => {
    // Shuffle tasks for the playing phase
    const shuffledTasks = shuffleArray([...currentScenario.tasks]);
    setCurrentTasks(shuffledTasks);
    setGameState(GAME_STATES.PLAYING);
  };

  const handleReorder = (newTasks) => {
    setCurrentTasks(newTasks);
  };

  const calculateScore = (userOrder) => {
    let score = 0;
    userOrder.forEach((task, index) => {
      const userPosition = index + 1;
      if (userPosition === task.correctPosition) {
        score += 2; // Exact match
      } else if (Math.abs(userPosition - task.correctPosition) === 1) {
        score += 1; // Adjacent position (partial credit)
      }
    });
    return score;
  };

  const handleSubmit = () => {
    const score = calculateScore(currentTasks);
    const maxScore = 8; // 4 tasks * 2 points each
    setScenarioScore(score);
    setTotalScore(prev => prev + score);
    setMaxPossibleScore(prev => prev + maxScore);
    setScenarioResults(prev => [...prev, { score, maxScore, userOrder: [...currentTasks] }]);
    setGameState(GAME_STATES.FEEDBACK);
  };

  const handleNextScenario = () => {
    if (currentScenarioIndex >= scenarios.length - 1) {
      setGameState(GAME_STATES.FINISHED);
    } else {
      setCurrentScenarioIndex(prev => prev + 1);
      setStudyTimeLeft(30);
      setGameState(GAME_STATES.STUDYING);
    }
  };

  const handleRestart = () => {
    setCurrentScenarioIndex(0);
    setTotalScore(0);
    setMaxPossibleScore(0);
    setScenarioResults([]);
    setStudyTimeLeft(30);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  const handleFinish = () => {
    const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
    onComplete?.({
      score: totalScore,
      maxScore: maxPossibleScore,
      accuracy: percentage,
      timeSpent: 0, // Sequencing game doesn't track time
      level,
      scenarioResults,
    });
  };

  // Render based on game state
  if (gameState === GAME_STATES.READY) {
    return (
      <div className="max-w-md mx-auto">
        <DeviceModeSelector onSelect={handleDeviceSelect} />
      </div>
    );
  }

  if (gameState === GAME_STATES.INSTRUCTIONS) {
    return (
      <div className="max-w-lg mx-auto">
        <InstructionsScreen
          level={level}
          onStart={handleStartTest}
          deviceMode={deviceMode}
        />
      </div>
    );
  }

  if (gameState === GAME_STATES.STUDYING) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-4 text-center">
          <Badge variant="outline">Scenario {currentScenarioIndex + 1} of {scenarios.length}</Badge>
        </div>
        <StudyPhase
          rules={currentScenario.rules}
          timeLeft={studyTimeLeft}
          onTimeUp={handleStudyComplete}
        />
      </div>
    );
  }

  if (gameState === GAME_STATES.PLAYING) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-4 text-center">
          <Badge variant="outline">Scenario {currentScenarioIndex + 1} of {scenarios.length}</Badge>
        </div>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <TaskOrdering
              tasks={currentTasks}
              onReorder={handleReorder}
              onSubmit={handleSubmit}
              canSubmit={true}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (gameState === GAME_STATES.FEEDBACK) {
    return (
      <div className="max-w-lg mx-auto">
        <FeedbackScreen
          scenario={currentScenario}
          userOrder={currentTasks}
          score={scenarioScore}
          maxScore={8}
          onNext={handleNextScenario}
          isLastScenario={currentScenarioIndex >= scenarios.length - 1}
        />
      </div>
    );
  }

  if (gameState === GAME_STATES.FINISHED) {
    const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
    return (
      <div className="max-w-lg mx-auto">
        <Card className="border-slate-200">
          <CardContent className="pt-8 text-center">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${percentage >= 75 ? 'bg-green-100' : percentage >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
              <ListOrdered className={`w-10 h-10 ${percentage >= 75 ? 'text-green-600' : percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
            </div>

            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Test Complete!
            </h2>

            <div className="text-4xl font-bold text-[#0B1D51] mb-2">
              {percentage}%
            </div>

            <p className="text-[#64748B] mb-6">
              You scored {totalScore} out of {maxPossibleScore} points
            </p>

            <div className="grid grid-cols-5 gap-2 mb-6">
              {scenarioResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg text-center ${result.score >= 6 ? 'bg-green-100 text-green-700' : result.score >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}
                >
                  <div className="text-xs font-medium">S{index + 1}</div>
                  <div className="text-sm font-bold">{result.score}/{result.maxScore}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleRestart}
                data-testid="restart-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white"
                onClick={handleFinish}
                data-testid="finish-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Score
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default SequencingGame;
